import { Box, Typography, Table, TableBody, TableCell, TableRow } from '@mui/material';

const deriveInitials = (name) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');

// What a tenant's initials cell shows on a given page. Manually adopted
// initials appear only on pages the tenant actually initialed; legacy records
// (no per-page data) show them everywhere, and pre-feature leases fall back to
// name-derived initials once signed.
const initialsForPage = (tenant, page) => {
  const manual = (tenant.initials || '').trim();
  if (manual) {
    const pages = tenant.initialed_pages || [];
    return !pages.length || page == null || pages.includes(page) ? manual : '';
  }
  return tenant.signature && tenant.signature.trim() !== '' ? deriveInitials(tenant.name) : '';
};

const Footer = ({ values, page, currentTenantId, onApplyInitials, highlight }) => {
  const landlordName = values?.manager
    ? `${values.manager.first_name || ''} ${values.manager.last_name || ''}`.trim()
    : '';
  const landlordInitials = landlordName ? deriveInitials(landlordName) : 'N/A';

  const tenants = values?.tenants || [];
  const currentTenant = currentTenantId
    ? tenants.find((t) => String(t.id) === String(currentTenantId))
    : null;
  const otherInitials = tenants
    .filter((t) => !currentTenant || String(t.id) !== String(currentTenantId))
    .map((t) => initialsForPage(t, page))
    .filter(Boolean)
    .join(', ');

  const isInitialed = !!currentTenant?.initialed_pages?.includes(page);
  const adopted = (currentTenant?.initials || '').trim();

  return (
    <Box
      className="lease-form-footer"
      position="relative"
      display="flex"
      justifyContent="flex-end"
      alignItems="center"
      sx={{ mb: 2, minHeight: 60 }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" fontSize="0.75rem">
          Page {page}
        </Typography>
      </Box>

      <Box display="flex" alignItems="center">
        <Box textAlign="center" mr={2}>
          <Typography variant="caption" fontSize="0.7rem">
            INITIALS
          </Typography>
        </Box>
        <Table
          size="small"
          sx={{ width: currentTenant ? '180px' : '120px', border: '1px solid', borderColor: 'grey.500' }}
        >
          <TableBody>
            <TableRow>
              <TableCell
                align="center"
                sx={{
                  borderRight: '1px solid',
                  borderColor: 'grey.500',
                  fontSize: '0.8rem',
                  p: 0.5,
                }}
              >
                Landlord
              </TableCell>
              <TableCell align="center" sx={{ fontSize: '0.8rem', p: 0.5 }}>
                Tenant(s)
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                align="center"
                sx={{
                  borderRight: '1px solid',
                  borderColor: 'grey.500',
                  fontSize: '0.9rem',
                  p: 0.5,
                }}
              >
                {landlordInitials}
              </TableCell>
              <TableCell align="center" sx={{ fontSize: '0.9rem', p: 0.5 }}>
                <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                  {currentTenant && (
                    isInitialed ? (
                      <Box
                        component="span"
                        onClick={() => onApplyInitials && onApplyInitials(page)}
                        title="Click to remove your initials from this page"
                        sx={{
                          cursor: 'pointer',
                          fontFamily: '"Brush Script MT", "Segoe Script", cursive',
                          fontSize: '1.15rem',
                          fontWeight: 600,
                          color: '#1a237e',
                          lineHeight: 1,
                          px: 0.5,
                          borderBottom: '1px solid #9e9e9e',
                          '&:hover': { background: '#FFF8E1' },
                        }}
                      >
                        {adopted}
                      </Box>
                    ) : (
                      <button
                        type="button"
                        id={`tenant-initials-page-${page}`}
                        className="tenant-initials-tab"
                        onClick={() => onApplyInitials && onApplyInitials(page)}
                        style={{
                          background: '#FDB813',
                          color: '#1d1d1f',
                          border: highlight ? '2px solid #d32f2f' : '1px solid #C28E0E',
                          boxShadow: highlight
                            ? '0 0 0 4px rgba(211,47,47,0.25)'
                            : '0 1px 2px rgba(0,0,0,0.2)',
                          borderRadius: 4,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          padding: '4px 10px',
                          cursor: 'pointer',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          transition: 'border 0.2s ease, box-shadow 0.2s ease',
                        }}
                      >
                        Initial
                      </button>
                    )
                  )}
                  {otherInitials && (
                    <Typography variant="caption" fontSize="0.85rem">
                      {currentTenant ? `, ${otherInitials}` : otherInitials}
                    </Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
      {currentTenant && !isInitialed && (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            right: 0,
            bottom: -14,
            color: highlight ? '#d32f2f' : '#B26A00',
            fontWeight: 600,
          }}
        >
          Click Initial to initial this page
        </Typography>
      )}
    </Box>
  );
};

export { Footer };
